import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate, useParams, Link } from "react-router-dom";
import useLenis from "@/lib/useLenis";
import { PROJECTS } from "@/lib/data";
import { normalizeImage, nativeAspectStyle } from "@/lib/imageUtils";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Cursor from "@/components/site/Cursor";

const EASE = [0.76, 0, 0.24, 1];

const Line = ({ children, delay = 0, className = "" }) => (
  <span className="line-mask">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
      className={className}
    >
      {children}
    </motion.span>
  </span>
);

const MetaRow = ({ label, value }) => (
  <div className="flex flex-col gap-1 border-b border-white/[0.08] py-3 md:py-4">
    <div className="mono-label">{label}</div>
    <div className="text-white text-sm md:text-base leading-snug">{value}</div>
  </div>
);

/*
  Case study page, respects each image's native aspect ratio on md+ so
  nothing is cropped. Mobile uses natural ratio too (single column full-bleed).
*/
export default function Case() {
  useLenis();
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = PROJECTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (project) document.title = `${project.title}, HANKDAVID`;
    window.scrollTo(0, 0);
  }, [project]);

  // Mobile hero override — must be declared BEFORE any early return so
  // hook order stays stable.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const factsSectionRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.4], ["0%", "-15%"]);
  const heroScale = useTransform(scrollYProgress, [0, 0.4], [1, 1.08]);

  if (!project) {
    return (
      <div className="App grain min-h-screen bg-[#0a0a0b] text-white flex items-center justify-center px-6">
        <div className="max-w-lg text-center">
          <span className="mono-label">Not found</span>
          <h1 className="font-display text-5xl md:text-7xl mt-6">Project missing.</h1>
          <button
            onClick={() => navigate("/")}
            className="mt-10 mono-label border-b border-white/40 hover:border-white pb-2"
            data-testid="case-back-home"
          >
            ← Return to studio
          </button>
        </div>
      </div>
    );
  }

  const hero = normalizeImage(project.images[0], project.title);
  const rest = project.images.slice(1).map((i, idx) => normalizeImage(i, `${project.title}, ${idx + 2}`));
  const copy = project.fullCopy || [project.story];
  const activeHero = isMobile && project.heroMobile ? project.heroMobile : hero;

  return (
    <div className="App grain bg-[#0a0a0b] text-white" data-testid={`case-page-${project.slug}`}>
      <Cursor />
      <Navbar />

      <main className="relative">
        {/* PINNED LAYER — hero image + title, stays sticky as user scrolls the case study */}
        <section
          data-testid="case-hero"
          className="sticky top-0 h-screen w-full overflow-hidden bg-[#0a0a0b] z-0"
        >
          <motion.img
            src={activeHero.src}
            alt={activeHero.alt}
            style={{ scale: heroScale }}
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
          />
          {project.video && !project.confidential && (
            <motion.video
              key={isMobile ? project.video : (project.videoDesktop || project.video)}
              src={isMobile ? project.video : (project.videoDesktop || project.video)}
              poster={activeHero.src}
              autoPlay
              loop
              muted
              playsInline
              style={{ scale: heroScale }}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="case-hero-video"
            />
          )}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "linear-gradient(180deg, rgba(10,10,11,0.55) 0%, rgba(10,10,11,0.15) 25%, rgba(10,10,11,0.15) 55%, rgba(10,10,11,0.85) 100%)",
            }}
          />

          {/* Title + intro paragraph, layered over the pinned hero — hidden for confidential projects until reveal */}
          {!project.confidential && (
            <div className="relative z-10 h-full px-6 md:px-10 pt-24 md:pt-28 pb-10 md:pb-14 flex flex-col justify-end">
              <h1
                data-testid="case-title"
                className={`font-display uppercase text-white leading-[0.9] tracking-[-0.03em] drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)] ${
                  project.title.length > 14
                    ? "text-[7vw] md:text-[3.6vw] lg:text-[3vw]"
                    : "text-[10vw] md:text-[6vw] lg:text-[5vw]"
                }`}
              >
                <Line delay={0.2}>{project.title}</Line>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                className="mt-6 md:mt-8 max-w-xl text-white/90 text-base md:text-lg leading-relaxed drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)] [text-wrap:balance]"
              >
                {project.story}
              </motion.p>
            </div>
          )}
        </section>

        {/* SCROLLING OVERLAY — fact sheet, narrative, CTA all scroll over the pinned hero */}
        <div className="relative z-10 -mt-[100vh] pointer-events-none">
          {/* Spacer so the pinned hero is fully visible on initial load before overlays begin */}
          <div className="h-screen w-full" aria-hidden="true" />

          {/* Fact sheet — translucent so pinned hero still peeks through */}
          {!project.confidential && (
            <section
              ref={factsSectionRef}
              data-testid="case-facts"
              className="relative bg-[#0a0a0b]/85 backdrop-blur-md border-t border-white/[0.06] pointer-events-auto"
            >
              <div className="px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14">
                <div className="md:col-span-4">
                  <div className="mono-label">§ Fact Sheet</div>
                  <p className="mt-6 text-white/70 max-w-sm leading-relaxed">
                    A snapshot of the project, location, size, discipline, and status.
                  </p>
                </div>
                <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  <MetaRow label="Project" value={project.title} />
                  <MetaRow label="Location" value={project.location} />
                  <MetaRow label="Project Type" value={project.projectType || project.tag} />
                  <MetaRow label="Size" value={project.size || "-"} />
                  <MetaRow label="Country" value={project.country || "-"} />
                  <MetaRow label="Year of Completion" value={project.year} />
                  <MetaRow label="Status" value={project.status || "In Development"} />
                  <MetaRow label="Discipline" value={project.role} />
                </div>
              </div>
            </section>
          )}

        {/* Editorial copy + image interleave — notahotel/Snohetta-inspired quiet layout */}
        <section
          data-testid="case-narrative"
          className="relative bg-[#0a0a0b]/85 backdrop-blur-md border-t border-white/[0.06] pointer-events-auto"
        >
          {!project.confidential && copy.map((para, idx) => {
            const image = rest[idx];
            const label = String(idx + 1).padStart(2, "0");
            const imageOnRight = idx % 2 === 1;
            return (
              <div
                key={para.slice(0, 40)}
                className="px-6 md:px-10 py-20 md:py-36"
              >
                <div className="max-w-7xl mx-auto">
                  {/* Tiny section marker */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="mono-label text-white/40 mb-10 md:mb-14 tracking-[0.28em]"
                  >
                    {label} — {project.title}
                  </motion.div>

                  {/* Image + copy — alternating left/right, copy vertically centered against image */}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center">
                    {image && (
                      <motion.figure
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 1.2, ease: EASE }}
                        className={`md:col-span-8 ${imageOnRight ? "md:order-2" : ""}`}
                      >
                        <div
                          className="w-full overflow-hidden"
                          style={{ aspectRatio: "5 / 3" }}
                        >
                          <img
                            src={image.src}
                            alt={image.alt}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      </motion.figure>
                    )}
                    <motion.p
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-10%" }}
                      transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
                      className={`md:col-span-4 text-white/75 text-sm md:text-[15px] leading-[1.85] max-w-sm ${imageOnRight ? "md:order-1" : ""}`}
                    >
                      {para}
                    </motion.p>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Additional images without paired copy — same rhythm */}
          {!project.confidential && rest.slice(project.confidential ? 0 : copy.length).map((image, idx) => {
            const num = (project.confidential ? 0 : copy.length) + idx + 1;
            const label = String(num).padStart(2, "0");
            return (
              <div
                key={`extra-${idx}`}
                className="px-6 md:px-10 py-20 md:py-36"
              >
                <div className="max-w-5xl mx-auto">
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="mono-label text-white/40 mb-10 md:mb-16 tracking-[0.28em]"
                  >
                    {label} — {project.title}
                  </motion.div>
                  <motion.figure
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 1.2, ease: EASE }}
                    className="w-full"
                  >
                    <div
                      className="w-full overflow-hidden"
                      style={{ aspectRatio: "5 / 3" }}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </motion.figure>
                </div>
              </div>
            );
          })}
        </section>

        {/* CTA */}
        <section className="relative bg-[#0a0a0b]/85 backdrop-blur-md px-6 md:px-10 py-16 md:py-24 border-t border-white/[0.06] pointer-events-auto">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
            <div>
              <div className="mono-label">§ Next</div>
              <h2 className="font-display !normal-case text-[10vw] md:text-[6vw] lg:text-[5vw] mt-6 leading-[0.94] tracking-[-0.02em] max-w-[14ch]">
                Work With Us
              </h2>
            </div>
            <div className="flex flex-col md:flex-row gap-8">
              <button
                onClick={() => navigate("/")}
                data-testid="case-back-btn"
                data-cursor="←"
                className="mono-label border-b border-white/40 hover:border-white pb-2 text-left"
              >
                ← Back to studio
              </button>
              <Link
                to="/#contact"
                onClick={() => {
                  sessionStorage.setItem("hd-contact-intent", "project");
                  window.dispatchEvent(new CustomEvent("hankdavid-contact-intent", { detail: "project" }));
                }}
                data-testid="case-contact-btn"
                data-cursor="→"
                className="mono-label border border-white/20 hover:border-white/70 px-5 py-3 transition-colors"
              >
                Begin a project →
              </Link>
            </div>
          </div>
        </section>
        </div>{/* END scrolling overlay wrapper */}
      </main>

      <Footer />
    </div>
  );
}
